import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Row, Col, Space, Switch, Typography, Spin, Alert } from 'antd';
import { DesktopOutlined, FullscreenExitOutlined, FullscreenOutlined, SyncOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useFactoryFloorDisplay } from '@/hooks/useFactoryFloorDisplay';
import WorkOrderProgressCard from '@/components/manufacturing/factory-floor/WorkOrderProgressCard';
import type { FactoryFloorWorkOrderCard } from '@/services/factoryFloorDisplay.service';
import '@/components/manufacturing/factory-floor/factory-floor-display.css';

const { Title, Text } = Typography;

const PAGE_ROTATE_MS = 12000;
const CARD_MIN_FALLBACK = 280;
const CARD_HEIGHT_FALLBACK = 210;
const GAP_FALLBACK = 14;

function getFullscreenElement(): Element | null {
  return (
    document.fullscreenElement
    ?? (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement
    ?? null
  );
}

async function requestFullscreen(el: HTMLElement): Promise<void> {
  if (el.requestFullscreen) {
    await el.requestFullscreen();
    return;
  }
  const webkitEl = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
  if (webkitEl.webkitRequestFullscreen) {
    await webkitEl.webkitRequestFullscreen();
  }
}

async function exitFullscreen(): Promise<void> {
  if (document.exitFullscreen) {
    await document.exitFullscreen();
    return;
  }
  const webkitDoc = document as Document & { webkitExitFullscreen?: () => Promise<void> | void };
  if (webkitDoc.webkitExitFullscreen) {
    await webkitDoc.webkitExitFullscreen();
  }
}

function readCssPx(el: HTMLElement, name: string, fallback: number): number {
  const raw = getComputedStyle(el).getPropertyValue(name).trim();
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function estimateCardsPerPage(board: HTMLElement | null): number {
  if (!board) return 8;
  const width = board.clientWidth || 1200;
  const height = board.clientHeight || 700;
  const gap = readCssPx(board, '--ff-gap', GAP_FALLBACK);
  const minCol = readCssPx(board, '--ff-card-min', CARD_MIN_FALLBACK);
  const cols = Math.max(1, Math.floor((width + gap) / (minCol + gap)));
  const rows = Math.max(1, Math.floor((height + gap) / (CARD_HEIGHT_FALLBACK + gap)));
  return Math.max(1, cols * rows);
}

function chunkCards<T>(items: T[], size: number): T[][] {
  if (size <= 0) return [items];
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  return pages.length ? pages : [[]];
}

const FactoryFloorDisplayPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const tvMode = searchParams.get('mode') === 'tv' || searchParams.get('mode') === 'TV';
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pointerPaused, setPointerPaused] = useState(false);
  const [cardsPerPage, setCardsPerPage] = useState(8);
  const [pageIndex, setPageIndex] = useState(0);

  const refreshMs = autoRefresh ? 30000 : undefined;
  const { data = [], isLoading, isError, error, refetch, isFetching } = useFactoryFloorDisplay(refreshMs);

  useEffect(() => {
    if (!isFetching && !isLoading) {
      setLastUpdated(new Date());
    }
  }, [isFetching, isLoading, data]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(getFullscreenElement() === pageRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  const effectiveTvMode = tvMode || isFullscreen;

  const recomputeCapacity = useCallback(() => {
    setCardsPerPage(estimateCardsPerPage(boardRef.current));
  }, []);

  useEffect(() => {
    recomputeCapacity();
    const el = boardRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recomputeCapacity);
      return () => window.removeEventListener('resize', recomputeCapacity);
    }
    const ro = new ResizeObserver(() => recomputeCapacity());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recomputeCapacity, effectiveTvMode, isFullscreen]);

  const pageClass = useMemo(
    () =>
      `factory-floor-display${effectiveTvMode ? ' factory-floor-display--tv' : ''}${
        isFullscreen ? ' factory-floor-display--fullscreen' : ''
      }`,
    [effectiveTvMode, isFullscreen]
  );

  const toggleFullscreen = useCallback(async () => {
    const el = pageRef.current;
    if (!el) return;
    try {
      if (getFullscreenElement() === el) {
        await exitFullscreen();
      } else {
        await requestFullscreen(el);
      }
    } catch {
      // Browser may block fullscreen without user gesture or in unsupported contexts.
    }
  }, []);

  const toggleTv = () => {
    const next = new URLSearchParams(searchParams);
    if (tvMode) next.delete('mode');
    else next.set('mode', 'tv');
    setSearchParams(next);
  };

  const cards = data as FactoryFloorWorkOrderCard[];
  const pages = useMemo(() => chunkCards(cards, cardsPerPage), [cards, cardsPerPage]);
  const pageCount = pages.length;

  const cardsIdentity = useMemo(
    () => cards.map((c) => `${c.work_order_id}:${c.operations_health_status}:${c.progress_percent}`).join('|'),
    [cards]
  );

  useEffect(() => {
    setPageIndex(0);
  }, [cardsIdentity, cardsPerPage]);

  useEffect(() => {
    if (pageIndex >= pageCount) {
      setPageIndex(0);
    }
  }, [pageIndex, pageCount]);

  // Pause rotation on hover only outside pure TV/fullscreen (desktop inspection).
  const rotationPaused = pointerPaused && !effectiveTvMode;

  useEffect(() => {
    if (pageCount <= 1 || rotationPaused) return undefined;
    const id = window.setInterval(() => {
      setPageIndex((i) => (i + 1) % pageCount);
    }, PAGE_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [pageCount, rotationPaused]);

  return (
    <div
      ref={pageRef}
      className={pageClass}
      style={{ padding: effectiveTvMode ? 12 : 24 }}
      onMouseEnter={() => setPointerPaused(true)}
      onMouseLeave={() => setPointerPaused(false)}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={effectiveTvMode ? 3 : 4} style={{ margin: 0, color: effectiveTvMode ? '#f5f5f5' : undefined }}>
            Factory floor — work order progress
          </Title>
          <Text type={effectiveTvMode ? 'secondary' : undefined} style={{ color: effectiveTvMode ? '#a6a6a6' : undefined }}>
            Released, in progress, stage in progress, and inspection pending · Last updated{' '}
            {lastUpdated.toLocaleTimeString()}
            {cards.length ? ` · ${cards.length} active` : ''}
            {isFullscreen ? ' · Press Esc to exit fullscreen' : ''}
          </Text>
        </Col>
        <Col>
          <Space wrap>
            <span style={{ color: effectiveTvMode ? '#d9d9d9' : undefined }}>
              Auto-refresh
              <Switch checked={autoRefresh} onChange={setAutoRefresh} style={{ marginLeft: 8 }} />
            </span>
            <Button icon={<SyncOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refresh
            </Button>
            <Button icon={<DesktopOutlined />} onClick={toggleTv} type={tvMode ? 'primary' : 'default'}>
              {tvMode ? 'Exit TV mode' : 'TV mode'}
            </Button>
            <Button
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
              type={isFullscreen ? 'primary' : 'default'}
            >
              {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            </Button>
          </Space>
        </Col>
      </Row>

      {isError ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load factory floor board"
          description={(error as Error)?.message || 'Unknown error'}
        />
      ) : null}

      {isLoading ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" tip="Loading work orders..." />
        </div>
      ) : cards.length === 0 ? (
        <div className="factory-floor-display__empty">
          <Title level={4} style={{ color: effectiveTvMode ? '#d9d9d9' : undefined }}>
            No active work orders
          </Title>
          <Text style={{ color: effectiveTvMode ? '#a6a6a6' : undefined }}>
            Work orders in Released, In Progress, Stage In Progress, or Inspection Pending will appear here.
          </Text>
        </div>
      ) : (
        <div className="factory-floor-display__board" ref={boardRef}>
          <div className="factory-floor-display__pages">
            {pages.map((pageCards, idx) => (
              <div
                key={`page-${idx}`}
                className={`factory-floor-display__page${idx === pageIndex ? ' factory-floor-display__page--active' : ''}`}
                aria-hidden={idx !== pageIndex}
              >
                {pageCards.map((card) => (
                  <WorkOrderProgressCard key={card.work_order_id} card={card} tvMode={effectiveTvMode} />
                ))}
              </div>
            ))}
          </div>
          {pageCount > 1 ? (
            <div className="factory-floor-display__pager" aria-label={`Page ${pageIndex + 1} of ${pageCount}`}>
              {pages.map((_, idx) => (
                <button
                  key={`dot-${idx}`}
                  type="button"
                  className={`factory-floor-display__pager-dot${idx === pageIndex ? ' factory-floor-display__pager-dot--active' : ''}`}
                  aria-label={`Go to page ${idx + 1}`}
                  onClick={() => setPageIndex(idx)}
                />
              ))}
              <span className="factory-floor-display__pager-label">
                {pageIndex + 1} / {pageCount}
              </span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default FactoryFloorDisplayPage;
